import { useEffect, useRef, useState } from 'react';
import { Form, FormControlProps, InputGroup } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRangePicker, Range, StaticRange } from 'react-date-range';
import { MdClose } from 'react-icons/md';
import { AiOutlineCalendar } from 'react-icons/ai';
import { isMobile } from 'react-device-detect';
import { supportsPassiveOnEventListener } from '@utils/Polyfill';
import DATE_FORMATS from '@constants/dateFormat';
import { safeFormat } from '@utils/DateUtils';
import './style.css';

interface DatePickerProps
  extends FormControlProps,
    React.RefAttributes<HTMLElement> {
  setStart: (value: string) => void;
  setEnd: (value: string) => void;
  start?: Date;
  end?: Date;
  minDate?: Date;
  addMaxDate?: boolean;
  customStaticRange?: StaticRange[];
  validationError?: string;
  onChange?: (...event: any[]) => void;
  useGrayBackground?: boolean;
  calendarPosition?: 'left' | 'right';
  displayFormat?: string;
}

function ThDateRangePicker({
  setStart,
  setEnd,
  start,
  end,
  minDate,
  addMaxDate,
  validationError,
  customStaticRange,
  useGrayBackground,
  calendarPosition = 'left',
  displayFormat = DATE_FORMATS.DATE_FORMAT,
  ...props
}: DatePickerProps) {
  const { control } = useForm({
    defaultValues: {
      startDate: safeFormat(start, 'yyyy-MM-dd'),
      endDate: safeFormat(end, 'yyyy-MM-dd'),
    },
  });
  const { isInvalid, onChange, className, placeholder, ...restProps } = props;
  const controlClassName =
    [className, useGrayBackground ? 'th-date-range-picker-gray' : undefined]
      .filter(Boolean)
      .join(' ')
      .trim() || undefined;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRangePickerField, setDateRangePickerField] = useState('');
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);

  const componentRef = useRef<HTMLDivElement>(null);

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: start ? new Date(start) : undefined,
      endDate: end ? new Date(end) : undefined,
      key: 'selection',
    },
  ]);

  useEffect(() => {
    if (dateRange[0].startDate && dateRange[0].endDate) {
      const formattedStart = safeFormat(dateRange[0].startDate, displayFormat);
      const formattedEnd = safeFormat(dateRange[0].endDate, displayFormat);

      setStart(formattedStart);
      setEnd(formattedEnd);
      setDateRangePickerField(`${formattedStart} - ${formattedEnd}`);
    }
  }, [dateRange, setStart, setEnd, displayFormat]);

  // CLEAR SELECTION IF START AND END ARE undefined
  useEffect(() => {
    if (!start && !end) {
      setDateRange([
        {
          startDate: undefined,
          endDate: undefined,
          key: 'selection',
        },
      ]);
      setDateRangePickerField('');
    }
  }, [start, end]);

  const changeDate = (range: Range[]) => {
    if (onChange) {
      onChange(range);
    }
    setDateRange(range);
  };

  const handleNativeDateChange = (type: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validate future dates
    if (addMaxDate !== false && newDate > today) {
      if (type === 'start') {
        setStartDateError(true);
      } else {
        setEndDateError(true);
      }
      return;
    }

    // Validate date order
    if (
      type === 'end' &&
      dateRange[0].startDate &&
      newDate < dateRange[0].startDate
    ) {
      setEndDateError(true);
      return;
    }
    if (
      type === 'start' &&
      dateRange[0].endDate &&
      newDate > dateRange[0].endDate
    ) {
      setStartDateError(true);
      return;
    }

    // Clear errors if validation passes
    setStartDateError(false);
    setEndDateError(false);

    const newRange = [
      {
        startDate: type === 'start' ? newDate : dateRange[0].startDate,
        endDate: type === 'end' ? newDate : dateRange[0].endDate,
        key: 'selection',
      },
    ];

    setDateRange(newRange);

    // Update the display field for mobile view
    if (newRange[0].startDate && newRange[0].endDate) {
      const formattedStart = safeFormat(
        newRange[0].startDate,
        DATE_FORMATS.DATE_FORMAT_SHORT
      );
      const formattedEnd = safeFormat(
        newRange[0].endDate,
        DATE_FORMATS.DATE_FORMAT_SHORT
      );
      setDateRangePickerField(`${formattedStart} - ${formattedEnd}`);
    }

    changeDate(newRange);
  };

  const openDatePicker = (shouldOpen: boolean) => {
    setShowDatePicker(shouldOpen);
  };

  const clearSelection = () => {
    const newDateRange = [
      {
        startDate: undefined,
        endDate: undefined,
        key: 'selection',
      },
    ];
    setDateRange(newDateRange);
    setDateRangePickerField('');
    changeDate(newDateRange);
  };

  function useOutsideAlerter(ref: React.RefObject<HTMLDivElement>) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of component
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          openDatePicker(false);
        }
      }
      // Bind the event listener

      const supportsPassive = supportsPassiveOnEventListener();
      document.addEventListener(
        'mousedown',
        handleClickOutside,
        supportsPassive
          ? {
              passive: true,
            }
          : false
      );
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }
  useOutsideAlerter(componentRef);

  if (isMobile) {
    return (
      <div ref={componentRef} className="h-100">
        <InputGroup>
          <InputGroup.Text className="search-icon">
            <AiOutlineCalendar />
          </InputGroup.Text>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <div className="position-relative flex-grow-1">
                <Form.Control
                  type="date"
                  aria-label="Start Date"
                  data-testid="start-date-input-mobile"
                  className={controlClassName}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleNativeDateChange('start', e.target.value);
                  }}
                  onInput={(e) => {
                    field.onChange(e);
                    handleNativeDateChange('start', e.currentTarget.value);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleNativeDateChange('start', e.currentTarget.value);
                  }}
                  isInvalid={isInvalid || startDateError}
                  min={safeFormat(minDate, 'yyyy-MM-dd') && '2016-01-01'}
                  max={
                    addMaxDate === false
                      ? undefined
                      : safeFormat(new Date(), 'yyyy-MM-dd')
                  }
                  {...restProps}
                />
                {field.value && (
                  <MdClose
                    className="clear-icon cursor-point"
                    onClick={() => {
                      field.onChange('');
                      const newRange = [
                        {
                          startDate: undefined,
                          endDate: dateRange[0].endDate,
                          key: 'selection',
                        },
                      ];
                      setDateRange(newRange);
                      changeDate(newRange);
                      setStart('');
                    }}
                    data-testid="clear-start-date-icon"
                  />
                )}
              </div>
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <div className="position-relative flex-grow-1">
                <Form.Control
                  type="date"
                  aria-label="End Date"
                  data-testid="end-date-input-mobile"
                  className={controlClassName}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleNativeDateChange('end', e.target.value);
                  }}
                  onInput={(e) => {
                    field.onChange(e);
                    handleNativeDateChange('end', e.currentTarget.value);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleNativeDateChange('end', e.currentTarget.value);
                  }}
                  isInvalid={isInvalid || endDateError}
                  min={
                    dateRange[0].startDate
                      ? safeFormat(dateRange[0].startDate, 'yyyy-MM-dd')
                      : undefined
                  }
                  max={
                    addMaxDate === false
                      ? undefined
                      : safeFormat(new Date(), 'yyyy-MM-dd')
                  }
                  {...restProps}
                />
                {field.value && (
                  <MdClose
                    className="clear-icon cursor-point"
                    onClick={() => {
                      field.onChange('');
                      const newRange = [
                        {
                          startDate: dateRange[0].startDate,
                          endDate: undefined,
                          key: 'selection',
                        },
                      ];
                      setDateRange(newRange);
                      changeDate(newRange);
                      setEnd('');
                    }}
                    data-testid="clear-end-date-icon"
                  />
                )}
              </div>
            )}
          />
          {(isInvalid || startDateError || endDateError) && (
            <Form.Control.Feedback type="invalid">
              {startDateError &&
                'Start date cannot be after end date or in the future'}
              {endDateError &&
                'End date cannot be before start date or in the future'}
              {!startDateError && !endDateError && validationError}
            </Form.Control.Feedback>
          )}
        </InputGroup>
      </div>
    );
  }

  return (
    <div ref={componentRef} className="h-100" style={{ position: 'relative' }}>
      <InputGroup>
        <InputGroup.Text className="search-icon">
          <AiOutlineCalendar />
        </InputGroup.Text>
        <Form.Control
          value={dateRangePickerField}
          onClick={() => openDatePicker(true)}
          isInvalid={isInvalid}
          readOnly
          placeholder={placeholder ?? 'Select Date Range'}
          style={isInvalid ? { zIndex: 0 } : { fontSize: '14px' }}
          className={controlClassName}
          {...restProps}
        />
        {isInvalid && validationError && (
          <Form.Control.Feedback type="invalid">
            {validationError}
          </Form.Control.Feedback>
        )}
        {dateRangePickerField !== '' && (
          <MdClose
            className="clear-icon cursor-point"
            onClick={clearSelection}
            data-testid="clear-date-icon"
          />
        )}
      </InputGroup>
      {showDatePicker && (
        <div
          data-testid="date-range-picker"
          className={
            calendarPosition === 'right' ? 'calendar-position-right' : ''
          }
        >
          <DateRangePicker
            onChange={(item) => changeDate([item.selection])}
            ranges={dateRange}
            direction="horizontal"
            rangeColors={['var(--yellow)']}
            minDate={minDate ?? new Date(2016, 0, 1)}
            maxDate={addMaxDate === false ? undefined : new Date()}
            staticRanges={customStaticRange}
          />
        </div>
      )}
    </div>
  );
}

export default ThDateRangePicker;
